import React, { useState, useEffect } from "react";
import axios from "../axios";
import { toast } from "react-toastify";
import BarcodeScanner from "./BarcodeScanner";
import UXHint from "./UXHint";
import CommunityInsightBanner from "./CommunityInsightBanner";

/**
 * BarcodeProductManager
 * Permet au vendeur de :
 * 1. Rechercher un produit global par code-barres
 * 2. Le créer s'il n'existe pas encore
 * 3. Lier ce produit à son compte avec prix / stock / note
 *
 * Props :
 *   user          — objet utilisateur connecté (id, token)
 *   onSuccess     — callback optionnel appelé après un lien créé avec succès
 *   initialBarcode — (optionnel) pré-remplit le barcode et déclenche la recherche automatiquement
 */
const BarcodeProductManager = ({ user, onSuccess, initialBarcode }) => {
  // --- Champ de recherche ---
  const [barcode, setBarcode] = useState(initialBarcode || "");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // --- Résultat de la recherche ---
  const [foundProduct, setFoundProduct] = useState(null); // GlobalProduct existant
  const [notFound, setNotFound] = useState(false);        // true si 404
  const [communityData, setCommunityData] = useState(null); // données CommunityProduct si dispo

  // --- Formulaire création produit global ---
  const [createForm, setCreateForm] = useState({
    name: "",
    category: "",
    imageUrl: ""
  });
  const [creating, setCreating] = useState(false);

  // --- Formulaire lien vendeur ---
  const [linkForm, setLinkForm] = useState({
    price: "",
    stock: "en stock",
    customNote: ""
  });
  const [linking, setLinking] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState(false);

  const [showScanner, setShowScanner] = useState(false);

  const token = user?.token;
  const authHeader = { Authorization: `Bearer ${token}` };

  // ─────────────────────────────────────────
  // 1. Recherche par code-barres
  // ─────────────────────────────────────────

  // Logique de recherche extraite pour pouvoir être appelée programmatiquement
  // (ex: depuis initialBarcode via useEffect)
  const performSearch = async (value) => {
    const trimmed = value?.trim();
    if (!trimmed) return;

    setSearching(true);
    setFoundProduct(null);
    setNotFound(false);
    setSearchError(null);
    setLinkSuccess(false);
    setCommunityData(null);

    try {
      const res = await axios.get(`/global-products/barcode/${trimmed}`);
      setFoundProduct(res.data);
      console.log("✅ [Barcode] Produit global trouvé :", res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        console.log("ℹ️ [Barcode] Produit absent de la base — tentative lookup externe…");
        try {
          const lookupRes = await axios.post("/scans/lookup-barcode", { barcode: trimmed });
          if (lookupRes.data?.found && lookupRes.data?.globalProduct) {
            console.log("✅ [Barcode] Produit trouvé via lookup externe :", lookupRes.data.globalProduct.name);
            setFoundProduct(lookupRes.data.globalProduct);
            // Pas de setNotFound : on passe directement à l'état "Produit trouvé"
            return;
          }
        } catch (lookupErr) {
          console.warn("⚠️ [Barcode] lookup-barcode échoué :", lookupErr.message);
        }

        // Lookup externe aussi sans résultat → formulaire de création manuelle
        setNotFound(true);
        console.log("ℹ️ [Barcode] Produit introuvable partout, vérification données communautaires…");
        try {
          const communityRes = await axios.get(`/community-products/barcode/${trimmed}`);
          setCommunityData(communityRes.data);
          if (communityRes.data?.exists) {
            console.log(`👀 [Barcode] ${communityRes.data.count} client(s) ont repéré ce barcode`);
          }
        } catch (communityErr) {
          console.warn("⚠️ [Barcode] Impossible de charger les données communautaires :", communityErr.message);
        }
      } else {
        setSearchError("Erreur lors de la recherche. Réessaye.");
        console.error("❌ [Barcode] Erreur recherche :", err);
      }
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch(barcode);
  };

  const handleScanResult = (decoded) => {
    setShowScanner(false);
    setBarcode(decoded);
    performSearch(decoded);
  };

  // Si un barcode initial est fourni (retour depuis le flow "produit inconnu"),
  // déclencher automatiquement la recherche au montage
  useEffect(() => {
    if (initialBarcode?.trim()) {
      performSearch(initialBarcode.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────
  // 2. Création d'un produit global
  // ─────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) return;

    setCreating(true);
    try {
      const res = await axios.post(
        "/global-products",
        { barcode: barcode.trim(), ...createForm },
        { headers: authHeader }
      );
      setFoundProduct(res.data);
      setNotFound(false);
      console.log("✅ [Barcode] Produit global créé :", res.data);
    } catch (err) {
      if (err.response?.status === 409) {
        // Race condition : quelqu'un l'a créé entre-temps
        setFoundProduct(err.response.data.product);
        setNotFound(false);
      } else {
        console.error("❌ [Barcode] Erreur création produit :", err);
        toast.error("Erreur lors de la création du produit.");
      }
    } finally {
      setCreating(false);
    }
  };

  // ─────────────────────────────────────────
  // 3. Création du lien vendeur (VendorProductLink)
  // ─────────────────────────────────────────
  const handleLink = async (e) => {
    e.preventDefault();
    if (!foundProduct?._id) return;

    setLinking(true);
    try {
      await axios.post(
        "/vendor-links",
        {
          globalProductId: foundProduct._id,
          price: linkForm.price !== "" ? parseFloat(linkForm.price) : null,
          stock: linkForm.stock,
          customNote: linkForm.customNote
        },
        { headers: authHeader }
      );
      setLinkSuccess(true);
      console.log("✅ [Barcode] Lien vendeur créé");
      onSuccess?.();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.warn("Ce produit est déjà lié à votre compte.");
      } else {
        console.error("❌ [Barcode] Erreur création lien :", err);
        toast.error("Erreur lors de la création du lien vendeur.");
      }
    } finally {
      setLinking(false);
    }
  };

  // ─────────────────────────────────────────
  // Reset complet
  // ─────────────────────────────────────────
  const handleReset = () => {
    setBarcode("");
    setFoundProduct(null);
    setNotFound(false);
    setSearchError(null);
    setLinkSuccess(false);
    setCommunityData(null);
    setCreateForm({ name: "", category: "", imageUrl: "" });
    setLinkForm({ price: "", stock: "en stock", customNote: "" });
  };

  return (
    <div className="section-card">
      <h3 className="text-lg font-semibold mb-4">📦 Lier un produit par code-barres</h3>

      {/* --- Scanner caméra --- */}
      {showScanner && (
        <BarcodeScanner
          onScan={handleScanResult}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Aide scanner */}
      <UXHint
        id="barcode_scanner"
        text="Utilisez le bouton 📷 pour scanner un code-barres avec votre caméra au lieu de le saisir manuellement."
        variant="tip"
        style={{ marginBottom: "12px" }}
      />

      {/* --- Champ de recherche barcode --- */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="text"
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); performSearch(barcode); } }}
          placeholder="Code-barres EAN / GTIN"
          className="input-field"
          style={{ flex: 1, minWidth: "160px" }}
          disabled={searching}
        />
        <button
          type="button"
          onClick={() => setShowScanner((v) => !v)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "9px 14px",
            background: showScanner ? "#374151" : "#1e293b",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "0.875rem",
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          📷 {showScanner ? "Fermer" : "Scanner"}
        </button>
        <button
          type="button"
          onClick={() => performSearch(barcode)}
          disabled={searching || !barcode.trim()}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "9px 16px",
            background: searching || !barcode.trim() ? "#a5b4fc" : "#4f46e5",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: searching || !barcode.trim() ? "not-allowed" : "pointer",
            fontSize: "0.875rem",
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          {searching ? "⏳ Recherche…" : "🔍 Rechercher"}
        </button>
        {(foundProduct || notFound || linkSuccess) && (
          <button
            type="button"
            onClick={handleReset}
            style={{
              padding: "9px 14px",
              background: "#f1f5f9",
              color: "#475569",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            ✖ Réinitialiser
          </button>
        )}
      </div>

      {/* Erreur de recherche */}
      {searchError && (
        <p className="text-red-600 text-sm mb-3">{searchError}</p>
      )}

      {/* ─── Produit trouvé ─── */}
      {foundProduct && !linkSuccess && (
        <div className="border rounded-lg p-4 mb-4 bg-green-50">
          <p className="text-xs text-green-600 font-semibold uppercase mb-2">✅ Produit trouvé</p>

          <div className="flex gap-4 items-start">
            {/* Image si disponible */}
            {foundProduct.imageUrl && (
              <img
                src={foundProduct.imageUrl}
                alt={foundProduct.name}
                className="w-16 h-16 object-contain rounded border bg-white"
              />
            )}
            <div>
              <p className="font-bold text-gray-800">{foundProduct.name}</p>
              {foundProduct.category && (
                <p className="text-sm text-gray-500">🏷️ {foundProduct.category}</p>
              )}
              <p className="text-xs text-gray-400 mt-1">🔢 {foundProduct.barcode}</p>
            </div>
          </div>

          {/* Formulaire lien vendeur */}
          <form onSubmit={handleLink} className="mt-4 space-y-3">
            <p className="text-sm font-semibold text-gray-700">Configurer votre lien vendeur :</p>

            <div className="flex gap-3">
              <input
                type="number"
                min="0"
                step="0.01"
                value={linkForm.price}
                onChange={(e) => setLinkForm(prev => ({ ...prev, price: e.target.value }))}
                placeholder="Prix (€) — optionnel"
                className="input-field flex-1"
              />
              <select
                value={linkForm.stock}
                onChange={(e) => setLinkForm(prev => ({ ...prev, stock: e.target.value }))}
                className="input-field"
              >
                <option value="en stock">Disponible</option>
                <option value="promotion">Promotion</option>
                <option value="indisponible">Indisponible</option>
              </select>
            </div>

            <input
              type="text"
              value={linkForm.customNote}
              onChange={(e) => setLinkForm(prev => ({ ...prev, customNote: e.target.value }))}
              placeholder="Note personnalisée — optionnel"
              className="input-field w-full"
            />

            <button
              type="submit"
              disabled={linking}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              {linking ? "⏳ Enregistrement..." : "🔗 Lier ce produit à mon compte"}
            </button>
          </form>
        </div>
      )}

      {/* ─── Produit non trouvé → insight communautaire + formulaire de création ─── */}
      {notFound && !foundProduct && (
        <div>
          {/* Banner communautaire — visible uniquement si des clients ont repéré ce barcode */}
          <CommunityInsightBanner
            data={communityData}
            onReference={({ name, imageUrl }) => {
              setCreateForm(prev => ({
                ...prev,
                name: name || prev.name,
                imageUrl: imageUrl || prev.imageUrl,
              }));
            }}
          />

          <div className="border rounded-lg p-4 mb-4 bg-yellow-50">
          <p className="text-xs text-yellow-700 font-semibold uppercase mb-3">
            ⚠️ Produit inconnu — vous pouvez le créer
          </p>

          <form onSubmit={handleCreate} className="space-y-3">
            <input
              type="text"
              required
              value={createForm.name}
              onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Nom du produit *"
              className="input-field w-full"
            />
            <input
              type="text"
              value={createForm.category}
              onChange={(e) => setCreateForm(prev => ({ ...prev, category: e.target.value }))}
              placeholder="Catégorie — optionnel"
              className="input-field w-full"
            />
            <input
              type="url"
              value={createForm.imageUrl}
              onChange={(e) => setCreateForm(prev => ({ ...prev, imageUrl: e.target.value }))}
              placeholder="URL de l'image — optionnel"
              className="input-field w-full"
            />
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
            >
              {creating ? "⏳ Création..." : "➕ Créer ce produit global"}
            </button>
          </form>
        </div>
        </div>
      )}

      {/* ─── Succès du lien ─── */}
      {linkSuccess && (
        <div className="border rounded-lg p-4 bg-green-100 text-green-800 text-sm">
          🎉 Produit lié avec succès à votre compte !
          <button
            onClick={handleReset}
            className="ml-3 text-xs underline text-green-700"
          >
            Lier un autre produit
          </button>
        </div>
      )}
    </div>
  );
};

export default BarcodeProductManager;
